import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useTranslations } from '../i18n';
import { 
    ShieldCheck, 
    FileSearch, 
    Cog, 
    AlertTriangle, 
    CheckCircle, 
    MessageSquare, 
    Upload, 
    ArrowRight, 
    Check, 
    Sun, 
    Moon, 
    Mail, 
    Zap, 
    TrendingUp, 
    Activity,
    Info,
    ChevronRight,
    Search,
    Shield,
    Plus
} from 'lucide-react';
import { ImageUpload, ImageUploadHandle } from '../components/ui/ImageUpload';
import { LanguageSelector } from '../components/ui/LanguageSelector';
import { triggerAudit } from '../lib/r2';
import { SEO } from '../components/ui/SEO';
import { supabase } from '../lib/supabase';
import { generateUUID } from '../lib/uuid';
import { auditCaseStudies } from '../data/auditCaseStudies'; // Re-verify import

const favicon = '/favicon_clear.png';
const faviconDark = '/favicon_clear_dark.png';

interface AuditFile {
    url: string;
    thumbnail?: string;
    comment?: string;
    sizeBytes?: number;
}


export function AuditPage() {
    const navigate = useNavigate();
    const { preferences, setTheme } = usePreferences();
    const { t, language } = useTranslations();
    const { user, isAdmin } = useAuth();
    const isDark = preferences?.theme === 'dark';
    
    // Form state
    const [images, setImages] = useState<AuditFile[]>([]);
    const [email, setEmail] = useState('');
    const [carModel, setCarModel] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const [auditId, setAuditId] = useState<string | null>(null);
    const [auditStatus, setAuditStatus] = useState<string>('pending');
    const [lightningVerdict, setLightningVerdict] = useState<string | null>(null);
    const [lightningStatus, setLightningStatus] = useState<string | null>(null);
    const [resultData, setResultData] = useState<any>(null);

    const imageUploadRef = useRef<ImageUploadHandle>(null);

    // Polling for audit status updates
    const [debugStep, setDebugStep] = useState<string | null>(null);
    const [lastError, setLastError] = useState<string | null>(null);

    useEffect(() => {
        if (!isSuccess || !auditId || auditStatus === 'completed' || auditStatus === 'failed') return;

        const interval = setInterval(async () => {
            try {
                const { data, error: fetchError } = await supabase
                    .from('audit')
                    .select('status, result_json, debug_step, last_error')
                    .eq('id', auditId)
                    .single();

                if (fetchError) throw fetchError;

                if (data) {
                    setAuditStatus(data.status);
                    setDebugStep(data.debug_step);
                    setLastError(data.last_error);
                    
                    if (data.result_json?.lightning) {
                        if (data.result_json.lightning.verdict) setLightningVerdict(data.result_json.lightning.verdict);
                        if (data.result_json.lightning.status) setLightningStatus(data.result_json.lightning.status);
                    }

                    if (data.status === 'completed' && data.result_json) {
                        setResultData(data.result_json);
                        clearInterval(interval);
                    } else if (data.status === 'failed') {
                        clearInterval(interval);
                        setError(data.last_error || data.result_json?.error || (language === 'lv' ? 'Analīze neizdevās. Mēģiniet vēlreiz.' : 'Analysis failed. Please try again.'));
                    }
                }
            } catch (err) {
                console.error('Error polling audit status:', err);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [isSuccess, auditId, auditStatus]);

    const handleSubmit = async (e?: React.FormEvent, isTest = false) => {
        if (e) e.preventDefault();
        
        if (!email) {
            setError(t.audit.form.errorNoEmail);
            return;
        }

        if (images.length === 0 && !description) {
            setError(t.audit.form.errorNoFiles); // Or maybe a more specific error like "Please provide a description or upload a file"
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            // Updated to use the centralized worker endpoint for limit enforcement
            const response = await fetch(`${import.meta.env.VITE_WORKER_URL || 'https://mycarcosts.gtreulands.workers.dev'}/audit/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    carModel,
                    description,
                    language,
                    is_test: isTest,
                    userId: user?.id, // Pass logger in user ID if available
                    images: images.map((img, index) => ({
                        url: img.url,
                        display_order: index
                    }))
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (response.status === 429 && errorData.error === 'limitReached') {
                    const resetDate = errorData.resetAt ? new Date(errorData.resetAt).toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-US') : 'soon';
                    const errorMsg = language === 'lv' 
                        ? `Mēneša limits sasniegts (${errorData.usage}/${errorData.limit}). Limits resetosies: ${resetDate}. ${!errorData.isRegistered ? 'Reģistrējies, lai saņemtu vairāk.' : ''}`
                        : `Monthly limit reached (${errorData.usage}/${errorData.limit}). Reset date: ${resetDate}. ${!errorData.isRegistered ? 'Sign up to get 10 free audits.' : ''}`;
                    throw new Error(errorMsg);
                }
                throw new Error(errorData.error || 'Submission failed');
            }

            const data = await response.json();
            setAuditId(data.auditId);
            setIsSuccess(true);
            setAuditStatus('started');
            
        } catch (err: any) {
            console.error('Audit submission failed:', err);
            setError(err.message || (language === 'lv' ? 'Iesniegšana neizdevās. Lūdzu, mēģiniet vēlreiz.' : 'Submission failed. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-transparent font-primary text-slate-900 dark:text-slate-100 flex flex-col">
            <SEO />
            
            {/* Header - Reused from LandingPage */}
            <header className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-[#EEF5FB]/80 dark:bg-[#000D19]/80 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                        <img
                            src={isDark ? faviconDark : favicon}
                            alt="Logo"
                            className="w-8 h-8 object-contain"
                        />
                        <span className="text-xl font-bold tracking-tight">My Car Costs</span>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4">
                        <div>
                            <LanguageSelector variant="ghost" useModal={true} />
                        </div>
                        <button
                            onClick={() => setTheme(preferences?.theme === 'dark' ? 'light' : 'dark')}
                            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors text-slate-600 dark:text-slate-400 focus:outline-none"
                            aria-label="Toggle Theme"
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero / Form Section */}
                <section className="relative pt-16 pb-24 overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="max-w-3xl mx-auto flex flex-col items-center">
                            {/* Centered Top Section: Copy */}
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold text-xs mb-4">
                                    <Shield size={14} />
                                    {t.audit.hero.walletSecurity}
                                </div>
                                <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-4 leading-tight">
                                    {t.audit.hero.title}
                                </h1>
                                <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 mb-0 leading-relaxed font-medium">
                                    {t.audit.hero.subtitle}
                                </p>
                            </div>

                            {/* Centered Upload Form */}
                            <div className="w-full">
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2.5rem] blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                                    <div className="relative bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 md:p-10 border border-slate-200 dark:border-slate-800 shadow-2xl">
                                         {isSuccess ? (
                                            <div className="space-y-8 animate-fade-in py-6">
                                                <div className="text-center">
                                                    <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
                                                        {auditStatus === 'completed' ? (
                                                            <CheckCircle size={40} strokeWidth={3} className="animate-bounce-subtle" />
                                                        ) : (
                                                            <Activity size={40} strokeWidth={3} className="animate-pulse" />
                                                        )}
                                                    </div>
                                                    <h2 className="text-3xl font-black mb-4">
                                                        {auditStatus === 'completed' 
                                                            ? t.audit.form.successTitle 
                                                            : auditStatus === 'failed'
                                                                ? (language === 'lv' ? 'Analīze neizdevās' : 'Analysis Failed')
                                                                : (t.audit as any).progress.analyzing}
                                                    </h2>
                                                    <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed font-medium">
                                                        {auditStatus === 'completed'
                                                            ? t.audit.form.successDesc.replace('{email}', email)
                                                            : auditStatus === 'failed'
                                                                ? (language === 'lv' ? 'Mums radās problēma apstrādājot Jūsu pieprasījumu.' : 'We encountered a problem processing your request.')
                                                                : (t.audit as any).progress.statusDesc}
                                                    </p>
                                                </div>

                                                {/* Progress Tracker */}
                                                <div className="max-w-md mx-auto space-y-6">
                                                    <div className="relative h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                        <div 
                                                            className={`absolute top-0 left-0 h-full transition-all duration-1000 ease-out ${auditStatus === 'failed' ? 'bg-red-500' : 'bg-blue-600'}`}
                                                            style={{ 
                                                                width: auditStatus === 'completed' ? '100%' : 
                                                                       auditStatus === 'failed' ? '100%' :
                                                                       auditStatus === 'auditing_verdict' ? '70%' :
                                                                       auditStatus === 'analysing_repair' ? '35%' : '5%'
                                                            }}
                                                        />
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-1 gap-4">
                                                        {[
                                                            { id: 'analysing_repair', label: (t.audit as any).progress.checking },
                                                            { id: 'auditing_verdict', label: (t.audit as any).progress.roi },
                                                            { id: 'completed', label: (t.audit as any).progress.finalizing }
                                                        ].map((s, idx) => {
                                                            const isPast = [...['completed', 'finalizing', 'auditing_verdict', 'analysing_repair']].reverse().indexOf(auditStatus) > idx || (auditStatus === 'completed' && idx === 2);
                                                            const isActive = auditStatus === s.id && auditStatus !== 'completed';
                                                            
                                                            return (
                                                                <div key={s.id} className={`flex items-center gap-4 transition-all duration-500 ${isActive ? 'scale-105' : 'opacity-50'}`}>
                                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPast ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : isActive ? 'bg-blue-600 text-white animate-pulse' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                                                                        {isPast ? <Check size={18} strokeWidth={3} /> : <div className="text-xs font-black">{idx + 1}</div>}
                                                                    </div>
                                                                    <span className={`font-black text-sm uppercase tracking-wider ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                                                                        {s.label}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Admin Debug Info */}
                                                    {isAdmin && (
                                                        <div className="mt-8 p-4 bg-slate-100 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 font-mono text-[10px] text-slate-500 overflow-hidden">
                                                            <div className="flex justify-between mb-2">
                                                                <span className="font-bold uppercase opacity-50">Admin Debug</span>
                                                                <span className="animate-pulse text-blue-500">POLLING</span>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div>STEP: <span className="text-blue-600 dark:text-blue-400 font-bold">{debugStep || '...'}</span></div>
                                                                {lastError && (
                                                                    <div className="text-red-500 break-words">ERROR: {lastError}</div>
                                                                )}
                                                                <div>STATUS: {auditStatus}</div>
                                                                <div>ID: {auditId}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Lightning Verdict Card */}
                                                {lightningVerdict && (
                                                    <div className="mt-12 p-8 bg-blue-600 text-white rounded-[2.5rem] shadow-2xl shadow-blue-500/40 relative overflow-hidden group animate-slide-up">
                                                        <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
                                                            <Zap size={120} strokeWidth={1} />
                                                        </div>
                                                        <div className="relative z-10">
                                                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                                                                <Sun size={12} />
                                                                {(t.audit as any).progress.lightningBadge}
                                                            </div>
                                                            <h3 className="text-2xl font-black mb-4 leading-tight">
                                                                {(t.audit as any).progress.lightningTitle}
                                                            </h3>
                                                            <p className="text-lg font-bold text-blue-50 italic leading-relaxed">
                                                                "{lightningVerdict}"
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="pt-8 flex flex-col items-center gap-4">
                                                    <button
                                                        onClick={() => {
                                                            setIsSuccess(false);
                                                            setAuditStatus('pending');
                                                            setLightningVerdict(null);
                                                            setAuditId(null);
                                                        }}
                                                        className="px-8 py-4 bg-slate-900 dark:bg-slate-800 text-white rounded-2xl font-black hover:bg-slate-800 transition-all hover:scale-105 active:scale-95 shadow-xl"
                                                    >
                                                        {(t.audit as any).progress.anotherAudit}
                                                    </button>
                                                    <p className="text-sm text-slate-400 font-bold uppercase tracking-widest">
                                                        {(t.audit as any).progress.checkEmail}
                                                    </p>
                                                </div>

                                                {/* Deep Results Section (Roadmap + Price Check) */}
                                                {auditStatus === 'completed' && resultData && (
                                                    <div className="space-y-6 pt-4 animate-fade-in">
                                                        {/* Decision Punchline (Big, blunt action) */}
                                                        {resultData.auditor?.decision_punchline && (
                                                            <div className="p-8 rounded-[2.5rem] bg-slate-900 text-white border-4 border-blue-600 shadow-2xl animate-slide-up">
                                                                <div className="flex items-center gap-3 mb-4">
                                                                    <ShieldCheck className="text-blue-500" size={32} />
                                                                    <div className="text-xs font-black uppercase tracking-widest text-blue-400">{t.audit.caseStudy.officialAudit}</div>
                                                                </div>
                                                                <h3 className="text-3xl font-black leading-tight">
                                                                    {resultData.auditor.decision_punchline}
                                                                </h3>
                                                            </div>
                                                        )}

                                                        {/* Savings Pot Hook */}
                                                        {resultData.analyst?.savings_potential > 0 && (
                                                            <div className="p-6 rounded-[2rem] bg-emerald-600 text-white shadow-xl shadow-emerald-500/20 flex items-center justify-between group animate-scale-in">
                                                                <div>
                                                                    <div className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-1">{t.audit.results.savingsPotentialFound}</div>
                                                                    <div className="text-2xl font-black">€{resultData.analyst.savings_potential} {t.audit.results.potentialSaving}</div>
                                                                </div>
                                                                <TrendingUp size={40} className="opacity-20 group-hover:scale-110 transition-transform" />
                                                            </div>
                                                        )}

                                                        {/* Price Reality Check Card */}
                                                        {resultData.analyst?.price_verdict && (
                                                            <div className="p-6 rounded-[2rem] bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-sm">
                                                                <div className="flex items-center justify-between mb-4">
                                                                    <div className="text-xs font-black uppercase tracking-widest text-slate-500">{(t.audit as any).results.priceRealityCheck}</div>
                                                                    <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                                                        resultData.analyst.verdict === 'FAIR' ? 'bg-emerald-100 text-emerald-600' :
                                                                        resultData.analyst.verdict === 'RISKY' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
                                                                    }`}>
                                                                        {resultData.analyst.verdict}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-4 mb-4">
                                                                    <div className="flex-1 h-3 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                                                                        <div 
                                                                            className={`h-full transition-all duration-1000 ${
                                                                                (resultData.analyst.price_score || 0) > 80 ? 'bg-emerald-500' :
                                                                                (resultData.analyst.price_score || 0) > 50 ? 'bg-yellow-500' : 'bg-red-500'
                                                                            }`}
                                                                            style={{ width: `${resultData.analyst.price_score || 0}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="font-black text-xl">{resultData.analyst.price_score || 0}%</span>
                                                                </div>
                                                                <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                                                                    {resultData.analyst.summary}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Negotiation Script Section */}
                                                        {resultData.analyst?.negotiation_points?.length > 0 && (
                                                            <div className="p-8 rounded-[2rem] bg-white dark:bg-slate-900 border-2 border-dashed border-slate-300 dark:border-slate-700">
                                                                <div className="flex items-center gap-2 mb-6">
                                                                    <MessageSquare className="text-blue-600" size={20} />
                                                                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t.audit.results.negotiationScript}</div>
                                                                </div>
                                                                <div className="space-y-4">
                                                                    {resultData.analyst.negotiation_points.map((point: string, idx: number) => (
                                                                        <div key={idx} className="flex gap-4 group">
                                                                            <div className="w-6 h-6 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 font-black text-[10px] group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                                                {idx + 1}
                                                                            </div>
                                                                            <p className="text-sm font-bold italic text-slate-700 dark:text-slate-300">
                                                                                "{point}"
                                                                            </p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Roadmap Teaser Card */}
                                                        {resultData.auditor?.future_roadmap_teaser && (
                                                            <div className="p-8 rounded-[2rem] bg-indigo-600 text-white shadow-xl shadow-indigo-500/20 relative overflow-hidden group">
                                                                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform">
                                                                    <TrendingUp size={80} strokeWidth={1} />
                                                                </div>
                                                                <div className="relative z-10">
                                                                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                                                                        <Activity size={12} />
                                                                        {(t.audit as any).results.roadmapBadge}
                                                                    </div>
                                                                    <h4 className="text-xl font-black mb-3 leading-tight">{(t.audit as any).results.roadmapTitle}</h4>
                                                                    <p className="text-indigo-50 font-bold italic leading-relaxed text-base mb-6">
                                                                        "{resultData.auditor.future_roadmap_teaser}"
                                                                    </p>
                                                                    <button 
                                                                        onClick={() => window.open('/pricing', '_blank')}
                                                                        className="w-full py-3 bg-white text-indigo-600 rounded-xl font-black text-sm hover:bg-white/90 transition-all flex items-center justify-center gap-2"
                                                                    >
                                                                        {(t.audit as any).results.unlockFullReport}
                                                                        <ArrowRight size={16} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSubmit} className="space-y-6">
                                                {/* Primary Input: The Oracle Box */}
                                                <div className="space-y-4">
                                                    <div className="relative group/oracle">
                                                        <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl blur opacity-20 group-focus-within/oracle:opacity-40 transition duration-500"></div>
                                                        <div className="relative">
                                                            <textarea
                                                                required
                                                                placeholder={t.audit.form.descPlaceholder || "Paste your mechanic quote, describe the noise, or tell us the problem..."}
                                                                value={description}
                                                                rows={8}
                                                                onChange={(e) => setDescription(e.target.value)}
                                                                className="w-full px-6 py-6 rounded-3xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:ring-0 focus:border-blue-500 outline-none transition-all font-bold text-lg resize-none shadow-inner"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Secondary Info: Accuracy Boosters */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <div className="space-y-1.5">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <Mail size={12} className="text-slate-400" />
                                                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                                                    {t.audit.form.emailLabel}
                                                                </label>
                                                            </div>
                                                            <input
                                                                type="email"
                                                                required
                                                                placeholder={t.audit.form.emailPlaceholder}
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                className="w-full px-5 py-3 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-bold text-sm"
                                                            />
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <Cog size={12} className="text-slate-400" />
                                                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                                                    {t.audit.form.carLabel}
                                                                </label>
                                                            </div>
                                                            <input
                                                                type="text"
                                                                required
                                                                placeholder={t.audit.form.carPlaceholder}
                                                                value={carModel}
                                                                onChange={(e) => setCarModel(e.target.value)}
                                                                className="w-full px-5 py-3 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-bold text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Image Previews */}
                                                {images.length > 0 && (
                                                    <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                                                        <ImageUpload
                                                            ref={imageUploadRef}
                                                            images={images as any}
                                                            onChange={(newImages) => setImages(newImages as any)}
                                                            folder="audit"
                                                            maxImages={10}
                                                            type="expense"
                                                            horizontal={true}
                                                            hideUploadButton={true}
                                                        />
                                                    </div>
                                                )}

                                                {error && (
                                                    <div className="p-4 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-2xl text-sm font-bold flex gap-3 animate-head-shake">
                                                        <AlertTriangle size={18} className="shrink-0" />
                                                        {error}
                                                    </div>
                                                )}

                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="w-full py-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-3xl font-black text-xl transition-all shadow-xl shadow-blue-500/30 flex flex-col items-center justify-center gap-1 hover:scale-[1.02] active:scale-[0.98]"
                                                >
                                                    {isSubmitting ? (
                                                        <div className="flex items-center gap-3">
                                                            <Activity size={24} className="animate-pulse" />
                                                            {t.audit.form.submitting}
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="flex items-center gap-3">
                                                                {t.audit.form.submit}
                                                                <ArrowRight size={24} />
                                                            </div>
                                                            <div className="text-[10px] font-black uppercase tracking-widest opacity-60">{t.audit.form.verifiedBy}</div>
                                                        </>
                                                    )}
                                                </button>

                                                {(['localhost', '127.0.0.1'].includes(window.location.hostname) || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('172.')) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSubmit(undefined, true)}
                                                        disabled={isSubmitting}
                                                        className="w-full py-3 text-slate-400 hover:text-slate-600 font-black text-[10px] uppercase tracking-widest transition-all"
                                                    >
                                                        {isSubmitting ? '...' : 'Dev: Skip Email Notification'}
                                                    </button>
                                                )}
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Background decoration - Reused from LandingPage */}
                    <div className="absolute top-0 inset-x-0 h-full overflow-hidden -z-10 pointer-events-none">
                        <div className="absolute left-[calc(50%-15rem)] top-10 w-[50rem] h-[50rem] bg-gradient-to-tr from-blue-100 to-purple-100 dark:from-blue-900/10 dark:to-purple-900/10 rounded-full blur-3xl opacity-60"></div>
                        <div className="absolute right-[calc(50%-20rem)] top-[20rem] w-[40rem] h-[40rem] bg-gradient-to-bl from-teal-100 to-blue-100 dark:from-teal-900/10 dark:to-blue-900/10 rounded-full blur-3xl opacity-60"></div>
                    </div>
                </section>

                {/* Real Advice / Case Studies Section */}
                <section className="py-24 bg-[#F8FBFE] dark:bg-slate-900/20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                            <div className="max-w-2xl">
                                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                                    <Activity size={12} />
                                    {t.audit.results.realWorldInsights}
                                </div>
                                <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
                                    {t.audit.results.oracleAdvice}
                                </h2>
                            </div>
                            <p className="text-lg text-slate-500 dark:text-slate-400 font-medium max-w-sm">
                                {t.audit.results.oracleAdviceSub}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {auditCaseStudies.map((study, idx) => (
                                <div key={idx} className="group relative">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2rem] blur opacity-0 group-hover:opacity-10 transition duration-500"></div>
                                    <div 
                                        onClick={() => navigate(`/audit/case-study/${study.slug}`)}
                                        className="relative h-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-8 flex flex-col shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
                                    >
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="p-3 bg-slate-50 dark:bg-slate-950 rounded-2xl">
                                                {study.icon}
                                            </div>
                                            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                                study.verdict === 'LETHAL' ? 'bg-red-100 text-red-600' :
                                                study.verdict === 'REPAIRABLE' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
                                            }`}>
                                                {study.verdict}
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{study.category}</div>
                                            <h3 className="text-xl font-black leading-tight group-hover:text-blue-600 transition-colors">{study.title}</h3>
                                        </div>

                                        <div className="space-y-4 mb-8 flex-1">
                                            <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl italic text-sm text-slate-600 dark:text-slate-400 font-medium border-l-4 border-slate-200 dark:border-slate-800">
                                                "{study.question}"
                                            </div>
                                            <p className="text-sm font-bold text-slate-800 dark:text-slate-200 leading-relaxed mb-6">
                                                {study.summary}
                                            </p>
                                            
                                            <div className="mt-auto">
                                                <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-blue-600 group-hover:gap-3 transition-all">
                                                    {t.audit.results.readFullAudit}
                                                    <ChevronRight size={14} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t.audit.results.potentialSaving}</div>
                                            <div className="text-lg font-black text-emerald-600">{study.savings}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="mt-16 text-center">
                            <button 
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-blue-600 hover:text-blue-700 transition-colors group"
                            >
                                {t.audit.results.getYourOwn}
                                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* How It Works Section */}
                <section className="py-24 bg-white dark:bg-slate-950/50 border-y border-slate-100 dark:border-slate-900">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight">{t.audit.howItWorks.title}</h2>
                            <p className="text-xl text-slate-600 dark:text-slate-400 font-medium">{t.audit.howItWorks.subtitle}</p>
                        </div>

                        <div className="grid md:grid-cols-4 gap-8 relative">
                            {/* Decorative line for desktop */}
                            <div className="hidden md:block absolute top-[60px] left-[15%] right-[15%] h-1 bg-slate-100 dark:bg-slate-800 -z-0"></div>
                            
                            <div className="flex flex-col items-center text-center group z-10">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform ring-4 ring-white dark:ring-slate-950">
                                    <FileSearch size={32} />
                                </div>
                                <h3 className="text-xl font-black mb-3">{t.audit.howItWorks.extract.title}</h3>
                                <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.howItWorks.extract.desc}</p>
                            </div>

                            <div className="flex flex-col items-center text-center group z-10">
                                <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform ring-4 ring-white dark:ring-slate-950">
                                    <Cog size={32} />
                                </div>
                                <h3 className="text-xl font-black mb-3">{t.audit.howItWorks.analyze.title}</h3>
                                <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.howItWorks.analyze.desc}</p>
                            </div>

                            <div className="flex flex-col items-center text-center group z-10">
                                <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform ring-4 ring-white dark:ring-slate-950">
                                    <ShieldCheck size={32} />
                                </div>
                                <h3 className="text-xl font-black mb-3">{t.audit.howItWorks.audit.title}</h3>
                                <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.howItWorks.audit.desc}</p>
                            </div>

                            <div className="flex flex-col items-center text-center group z-10">
                                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform ring-4 ring-white dark:ring-slate-950">
                                    <MessageSquare size={32} />
                                </div>
                                <h3 className="text-xl font-black mb-3">{t.audit.howItWorks.verdict.title}</h3>
                                <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.howItWorks.verdict.desc}</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why Use This / Benefits Section */}
                <section className="py-24 bg-transparent overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row items-center gap-16">
                            <div className="flex-1 space-y-12">
                                <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
                                    {(t.audit as any).demo.benefitsTitle}
                                </h2>
                                
                                <div className="space-y-8">
                                    <div className="flex gap-6">
                                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center shrink-0">
                                            <TrendingUp size={24} />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-black mb-2">{t.audit.valueProps.negotiation.title}</h4>
                                            <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.valueProps.negotiation.desc}</p>
                                        </div>
                                    </div>

                                    <div className="flex gap-6">
                                        <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center shrink-0">
                                            <Activity size={24} />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-black mb-2">{t.audit.valueProps.technical.title}</h4>
                                            <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.valueProps.technical.desc}</p>
                                        </div>
                                    </div>

                                    <div className="flex gap-6">
                                        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-xl flex items-center justify-center shrink-0">
                                            <Zap size={24} />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-black mb-2">{t.audit.valueProps.peace.title}</h4>
                                            <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.valueProps.peace.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Decorative Elements */}
                            <div className="flex-1 w-full max-w-[500px] relative">
                                <div className="absolute inset-0 bg-blue-600 dark:bg-blue-900/20 blur-[100px] opacity-20 -z-10"></div>
                                <div className="p-8 rounded-[3rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-3xl rotate-2 hover:rotate-0 transition-transform duration-500">
                                    <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100 dark:border-slate-800">
                                        <div className="font-black text-xl">{(t.audit as any).demo.title}</div>
                                        <div className="px-3 py-1 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-full text-xs font-black uppercase tracking-widest">{(t.audit as any).demo.verdictSell}</div>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl">
                                            <span className="font-bold">{(t.audit as any).demo.repairVsValue}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-400 line-through">$2,150</span>
                                                <span className="text-red-600 dark:text-red-400 font-black">48% of Value</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl">
                                            <span className="font-bold">{(t.audit as any).demo.cashGapSavings}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-blue-600 dark:text-blue-400 font-black">{(t.audit as any).demo.savingsValue}</span>
                                            </div>
                                        </div>
                                        <div className="p-4 bg-slate-900 text-white rounded-2xl shadow-xl shadow-slate-900/20">
                                            <div className="text-xs font-black uppercase tracking-widest opacity-80 mb-1">{(t.audit as any).demo.roiStrategyTitle}</div>
                                            <p className="font-medium text-sm text-slate-50">{(t.audit as any).demo.roiStrategyText}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Final CTA Section */}
                <section className="py-24 relative overflow-hidden">
                    <div className="absolute inset-0 bg-blue-600 dark:bg-blue-900/40">
                        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
                    </div>
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">{t.audit.cta.title}</h2>
                        <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto font-medium opacity-90">
                            {(t.audit as any).demo.ctaSubtext}
                        </p>
                        <button
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className="px-12 py-5 bg-white text-blue-600 rounded-2xl font-black text-xl hover:bg-blue-50 transition-all shadow-2xl hover:scale-105 active:scale-95"
                        >
                            {t.audit.cta.button}
                        </button>
                    </div>
                </section>
            </main>

            {/* Footer - Reused from LandingPage */}
            <footer className="border-t border-slate-200 dark:border-slate-800 bg-[#EEF5FB]/80 dark:bg-[#000D19]/80 backdrop-blur-md py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 gap-12">
                    <div className="col-span-1">
                        <div className="flex items-center gap-3 mb-4">
                            <img src={isDark ? faviconDark : favicon} alt="Logo" className="w-8 h-8 object-contain" />
                            <span className="font-bold text-xl text-slate-900 dark:text-white">My Car Costs</span>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            © 2026 My Car Costs
                        </p>
                    </div>

                    <div className="col-span-1">
                        <h4 className="font-bold mb-4 text-slate-900 dark:text-white uppercase tracking-wider text-xs opacity-80">{t.landing.footer.contact}</h4>
                        <div className="space-y-4">
                            <a href={`mailto:${t.landing.footer.contactEmail}`} className="block text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                {t.landing.footer.contactEmail}
                            </a>
                            <Link to="/privacy" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                {t.landing.footer.privacy}
                            </Link>
                            <Link to="/terms" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                {(t.landing as any).terms?.title || 'Terms and Conditions'}
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
